import { useEffect, useState } from 'react'
import { communicationService } from '../services/communication'
import type { WhatsAppTemplate } from '../../../types'
import { MessageSquare, Plus, X, Pencil, ToggleLeft, ToggleRight, Info, HelpCircle, ChevronDown, Wand2, Trash2 } from 'lucide-react'
import { ModalOverlay } from '../../../components/ModalOverlay'
import { useWindowWidth } from '../../../lib/useWindowWidth'

// ── CRM fields available for variable mapping ─────────────────────────────────

const CRM_FIELDS: { value: string; label: string; group: string }[] = [
  { value: 'customer_name',       label: 'Customer Name',        group: 'Lead'    },
  { value: 'mobile',              label: 'Mobile Number',        group: 'Lead'    },
  { value: 'vehicle_no',          label: 'Vehicle Reg No',       group: 'Lead'    },
  { value: 'vehicle_model',       label: 'Vehicle Model',        group: 'Lead'    },
  { value: 'date',                label: 'Insurance Due Date',   group: 'Lead'    },
  { value: 'insurance_due_date',  label: 'Insurance Due Date (full)', group: 'Lead' },
  { value: 'due_date',            label: 'Due Date (short)',      group: 'Lead'    },
  { value: 'policy_number',       label: 'Policy Number',        group: 'Lead'    },
  { value: 'insurance_company',   label: 'Insurance Company',    group: 'Lead'    },
  { value: 'premium_amount',      label: 'Premium Amount',       group: 'Lead'    },
  { value: 'payment_link',        label: 'Payment Link',         group: 'Lead'    },
  { value: 'amount',              label: 'Amount',               group: 'Lead'    },
  { value: 'insurance_company1',  label: 'Insurer 1',            group: 'Quotes'  },
  { value: 'idv1',                label: 'IDV 1',                group: 'Quotes'  },
  { value: 'premium_amount1',     label: 'Premium 1',            group: 'Quotes'  },
  { value: 'addons1',             label: 'Add-ons 1',            group: 'Quotes'  },
  { value: 'insurance_company2',  label: 'Insurer 2',            group: 'Quotes'  },
  { value: 'idv2',                label: 'IDV 2',                group: 'Quotes'  },
  { value: 'premium_amount2',     label: 'Premium 2',            group: 'Quotes'  },
  { value: 'addons2',             label: 'Add-ons 2',            group: 'Quotes'  },
  { value: 'dealer_name',         label: 'Dealer Name',          group: 'Dealer'  },
  { value: 'dealer_phone',        label: 'Dealer Phone Number',  group: 'Dealer'  },
  { value: 'dealer_domain',       label: 'Dealer Domain',        group: 'Dealer'  },
]

// ── Auto-detect named variables from body text ────────────────────────────────

// Normalise backend variable aliases so auto-detect always resolves to the canonical field name.
const VARIABLE_ALIASES: Record<string, string> = {
  vehicle_reg:    'vehicle_no',
  vehicle_reg_no: 'vehicle_no',
}

function detectVariables(body: string): Record<string, string> {
  const matches = [...body.matchAll(/\{\{([a-zA-Z_][a-zA-Z0-9_]*)\}\}/g)]
  const seen = new Set<string>()
  const result: Record<string, string> = {}
  let pos = 1
  for (const m of matches) {
    const name = VARIABLE_ALIASES[m[1]] ?? m[1]
    if (!seen.has(name)) {
      seen.add(name)
      result[String(pos++)] = name
    }
  }
  return result
}

const CATEGORIES = [
  { value: 'utility',        label: 'Utility'        },
  { value: 'marketing',      label: 'Marketing'      },
  { value: 'authentication', label: 'Authentication' },
]

const LANGUAGES = [
  { value: 'en', label: 'English'  },
  { value: 'hi', label: 'Hindi'    },
  { value: 'mr', label: 'Marathi'  },
  { value: 'gu', label: 'Gujarati' },
  { value: 'ta', label: 'Tamil'    },
  { value: 'te', label: 'Telugu'   },
]

// ── Variable mapping builder ──────────────────────────────────────────────────

const MAX_VARS = 12

function VariableMappingBuilder({
  value,
  onChange,
}: {
  value: Record<string, string>
  onChange: (v: Record<string, string>) => void
}) {
  const positions = Array.from(
    { length: Math.max(1, Object.keys(value).length) },
    (_, i) => String(i + 1),
  )

  const setVar = (pos: string, field: string) => {
    const next = { ...value }
    if (field) next[pos] = field
    else delete next[pos]
    onChange(next)
  }

  const addSlot = () => {
    const nextPos = String(positions.length + 1)
    if (positions.length < MAX_VARS) onChange({ ...value, [nextPos]: '' })
  }

  const removeSlot = (pos: string) => {
    const next = { ...value }
    delete next[pos]
    const reindexed: Record<string, string> = {}
    Object.values(next).forEach((v, i) => { reindexed[String(i + 1)] = v })
    onChange(reindexed)
  }

  return (
    <div className="space-y-2">
      {positions.map(pos => (
        <div key={pos} className="flex items-center gap-2">
          <span
            className="text-xs font-mono font-semibold px-2 py-1 rounded shrink-0"
            style={{ background: 'var(--brand-primary-subtle)', color: 'var(--brand-primary)', minWidth: 36, textAlign: 'center' }}
          >
            {`{{${pos}}}`}
          </span>
          <select
            className="input flex-1"
            value={value[pos] ?? ''}
            onChange={e => setVar(pos, e.target.value)}
          >
            <option value="">— select a CRM field —</option>
            {['Lead', 'Quotes', 'Dealer'].map(group => (
              <optgroup key={group} label={group}>
                {CRM_FIELDS.filter(f => f.group === group).map(f => (
                  <option key={f.value} value={f.value}>{f.label}</option>
                ))}
              </optgroup>
            ))}
          </select>
          {positions.length > 1 && (
            <button type="button" onClick={() => removeSlot(pos)} className="btn-icon shrink-0" title="Remove">
              <X className="h-3.5 w-3.5" style={{ color: 'var(--danger-text)' }} />
            </button>
          )}
        </div>
      ))}
      {positions.length < MAX_VARS && (
        <button type="button" onClick={addSlot} className="btn-secondary" style={{ fontSize: 12, height: 28 }}>
          <Plus className="h-3 w-3" /> Add variable
        </button>
      )}
    </div>
  )
}

// ── Variable mapping helpers ──────────────────────────────────────────────────

/**
 * Normalise variable_mapping to the positional dict format the form uses.
 * Handles both:
 *   Legacy:     { "body": ["customer_name", "vehicle_reg"] }
 *   Positional: { "1": "customer_name", "2": "vehicle_reg" }
 */
function toPositionalMapping(raw: Record<string, unknown> | null | undefined): Record<string, string> {
  if (!raw) return { '1': '' }
  if (Array.isArray(raw.body)) {
    const result: Record<string, string> = {}
    ;(raw.body as string[]).forEach((field, i) => { result[String(i + 1)] = field })
    return Object.keys(result).length > 0 ? result : { '1': '' }
  }
  const positional = raw as Record<string, string>
  return Object.keys(positional).length > 0 ? positional : { '1': '' }
}

// ── Template form modal ───────────────────────────────────────────────────────

interface TemplateForm {
  name: string
  category: string
  language: string
  body: string
  purpose: string
  variable_mapping: Record<string, string>
}

const emptyForm = (): TemplateForm => ({
  name: '', category: 'utility', language: 'en', body: '', purpose: '', variable_mapping: { '1': '' },
})

function TemplateFormModal({
  initial,
  onClose,
  onSaved,
}: {
  initial: WhatsAppTemplate | null
  onClose: () => void
  onSaved: (t: WhatsAppTemplate) => void
}) {
  const ext = initial as (WhatsAppTemplate & { purpose?: string }) | null
  const [form, setForm] = useState<TemplateForm>(
    initial
      ? {
          name: initial.name,
          category: initial.category.toLowerCase(),
          language: initial.language.toLowerCase(),
          body: initial.body ?? '',
          purpose: ext?.purpose ?? '',
          variable_mapping: toPositionalMapping(initial.variable_mapping),
        }
      : emptyForm(),
  )
  const [saving, setSaving] = useState(false)
  const [error, setError]   = useState('')

  const set = (k: keyof TemplateForm, v: string) => setForm(f => ({ ...f, [k]: v }))

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) { setError('Template name is required'); return }
    const cleanMapping: Record<string, string> = {}
    Object.entries(form.variable_mapping).forEach(([pos, field]) => {
      if (field) cleanMapping[pos] = field
    })
    setSaving(true)
    setError('')
    try {
      const payload = {
        name: form.name.trim().toLowerCase().replace(/\s+/g, '_'),
        category: form.category,
        language: form.language,
        body: form.body.trim() || undefined,
        purpose: form.purpose.trim() || undefined,
        variable_mapping: Object.keys(cleanMapping).length > 0 ? cleanMapping : undefined,
      }
      const saved = initial
        ? await communicationService.updateTemplate(initial.id, payload)
        : await communicationService.createTemplate(payload)
      onSaved(saved)
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail
      setError(typeof msg === 'string' ? msg : 'Failed to save template')
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalOverlay onClose={onClose}>
      <div className="modal-panel-flex" style={{ maxWidth: 560 }}>
        <div className="modal-flex-header">
          <div>
            <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
              {initial ? 'Edit Template' : 'Register Template'}
            </h2>
            <p className="text-xs mt-0.5" style={{ color: 'var(--text-tertiary)' }}>
              Template must already be approved on each dealer's Meta WABA
            </p>
          </div>
          <button className="btn-icon" onClick={onClose}><X className="h-4 w-4" /></button>
        </div>

        <div className="modal-flex-body">
          <div className="flex items-start gap-2 px-3 py-2.5 rounded-md"
            style={{ background: 'var(--info-bg)', color: 'var(--info-text)' }}>
            <Info className="h-4 w-4 mt-0.5 shrink-0" />
            <p className="text-xs leading-relaxed">
              The <strong>template name</strong> must match exactly what is registered on Meta.
              Variable positions <strong>{'{{1}}'}</strong>, <strong>{'{{2}}'}</strong>… must follow the same order as in the approved Meta template body.
            </p>
          </div>

          <form id="template-form" onSubmit={handleSubmit} className="space-y-4" style={{ marginTop: 16 }}>
            <div className="form-group">
              <label className="form-label">Template name *</label>
              <input
                className="input font-mono"
                placeholder="e.g. renewal_reminder"
                value={form.name}
                onChange={e => set('name', e.target.value)}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                Lowercase with underscores — must match Meta exactly
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="form-group">
                <label className="form-label">Category</label>
                <select className="input" value={form.category} onChange={e => set('category', e.target.value)}>
                  {CATEGORIES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Language</label>
                <select className="input" value={form.language} onChange={e => set('language', e.target.value)}>
                  {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
                </select>
              </div>
            </div>

            <div className="form-group">
              <div className="flex items-center justify-between mb-1">
                <label className="form-label" style={{ marginBottom: 0 }}>Message body</label>
                <button
                  type="button"
                  className="btn-secondary"
                  style={{ fontSize: 11, height: 26, padding: '0 8px', gap: 4 }}
                  onClick={() => {
                    const detected = detectVariables(form.body)
                    if (Object.keys(detected).length > 0)
                      setForm(f => ({ ...f, variable_mapping: detected }))
                  }}
                  disabled={!form.body.trim()}
                  title="Scan body for {{variable_name}} placeholders and auto-fill the mapping"
                >
                  <Wand2 className="h-3 w-3" /> Auto-detect variables
                </button>
              </div>
              <textarea
                className="input"
                rows={4}
                placeholder={"e.g. Hi {{customer_name}}, your {{vehicle_reg}} insurance is due for renewal."}
                value={form.body}
                onChange={e => set('body', e.target.value)}
                style={{ resize: 'vertical', fontFamily: 'inherit' }}
              />
              <p className="text-xs mt-1" style={{ color: 'var(--text-tertiary)' }}>
                Use {'{{variable_name}}'} for named placeholders — must match Meta exactly.
              </p>
            </div>

            <div className="form-group">
              <label className="form-label">Purpose</label>
              <input
                className="input"
                placeholder="e.g. Sent when policy renewal is due in 30 days"
                value={form.purpose}
                onChange={e => set('purpose', e.target.value)}
              />
            </div>

            <div className="form-group">
              <label className="form-label">Variable mapping</label>
              <p className="text-xs mb-2" style={{ color: 'var(--text-tertiary)' }}>
                Map each {'{{n}}'} in the Meta template to the CRM field that fills it at send time
              </p>
              <VariableMappingBuilder
                value={form.variable_mapping}
                onChange={v => setForm(f => ({ ...f, variable_mapping: v }))}
              />
            </div>

            {error && <div className="alert-danger text-sm">{error}</div>}
          </form>
        </div>

        <div className="modal-flex-footer">
          <button type="button" className="btn-secondary" onClick={onClose}>Cancel</button>
          <button type="submit" form="template-form" className="btn-primary" disabled={saving}>
            {saving ? 'Saving…' : initial ? 'Save Changes' : 'Register Template'}
          </button>
        </div>
      </div>
    </ModalOverlay>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function TemplatesPage() {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([])
  const [loading, setLoading]     = useState(true)
  const [formTarget, setFormTarget] = useState<WhatsAppTemplate | 'new' | undefined>(undefined)
  const [toggling, setToggling]   = useState<number | null>(null)
  const [deleting, setDeleting]   = useState<number | null>(null)
  const [showGuide, setShowGuide] = useState(() => localStorage.getItem('wa_templates_guide_dismissed') !== 'true')
  const [expandedVarsIds, setExpandedVarsIds] = useState<Set<number>>(new Set())
  const [expandedCardId, setExpandedCardId] = useState<number | null>(null)
  const isMobile = useWindowWidth() < 768

  const toggleExpandedVars = (id: number) => {
    setExpandedVarsIds(prev => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const dismissGuide = () => {
    setShowGuide(false)
    localStorage.setItem('wa_templates_guide_dismissed', 'true')
  }

  const load = async () => {
    setLoading(true)
    try { setTemplates(await communicationService.listTemplates()) }
    finally { setLoading(false) }
  }

  useEffect(() => { load() }, [])

  const handleToggle = async (t: WhatsAppTemplate) => {
    setToggling(t.id)
    try {
      const updated = await communicationService.toggleTemplate(t.id)
      setTemplates(prev => prev.map(x => x.id === t.id ? updated : x))
    } finally {
      setToggling(null)
    }
  }

  const handleDelete = async (t: WhatsAppTemplate) => {
    if (!confirm(`Delete "${t.name}"? This cannot be undone.`)) return
    setDeleting(t.id)
    try {
      await communicationService.deleteTemplate(t.id)
      setTemplates(prev => prev.filter(x => x.id !== t.id))
    } finally {
      setDeleting(null)
    }
  }

  const handleSaved = (saved: WhatsAppTemplate) => {
    setTemplates(prev => {
      const exists = prev.find(t => t.id === saved.id)
      return exists ? prev.map(t => t.id === saved.id ? saved : t) : [saved, ...prev]
    })
  }

  const getCrmLabel = (key: string) => CRM_FIELDS.find(f => f.value === key)?.label ?? key
  const activeCount   = templates.filter(t => t.is_active).length
  const inactiveCount = templates.filter(t => !t.is_active).length

  return (
    <div className="flex flex-col lg:h-full lg:min-h-0" style={{ gap: '24px' }}>

      <div className="section-header shrink-0">
        <div>
          <h1 className="page-title flex items-center gap-2">
            <MessageSquare className="h-6 w-6" style={{ color: 'var(--brand-primary)' }} />
            WhatsApp Templates
          </h1>
          <p className="page-subtitle">
            {loading ? 'Loading…' : `${activeCount} active · ${inactiveCount} inactive`}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!showGuide && (
            <button className="btn-secondary" onClick={() => setShowGuide(true)}>
              <HelpCircle className="h-4 w-4" /> Show guide
            </button>
          )}
          <button className="btn-primary" onClick={() => setFormTarget('new')}>
            <Plus className="h-4 w-4" /> Register Template
          </button>
        </div>
      </div>

      {/* How it works */}
      {showGuide && (
      <div className="card shrink-0" style={{ borderLeft: '3px solid var(--brand-primary)', position: 'relative' }}>
        <button
          onClick={dismissGuide}
          className="btn-icon"
          style={{ position: 'absolute', top: 12, right: 12 }}
          title="Dismiss guide"
        >
          <X className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
        </button>
        <p className="text-sm font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
          How to add a template
        </p>
        <ol className="text-sm space-y-1.5 list-decimal ml-4" style={{ color: 'var(--text-secondary)' }}>
          <li>Create the template on <strong>Meta Business Manager</strong> under the dealer's WhatsApp Business Account (WABA)</li>
          <li>Wait for Meta's approval — typically a few hours to a day</li>
          <li>Click <strong>Register Template</strong> here with the exact same name and map the {'{{variables}}'} to CRM fields</li>
          <li>Repeat step 1–2 for each dealer's WABA — the template name stays the same across all dealers</li>
          <li>Telecallers can now pick this template from the lead page — variables are filled automatically from the lead and dealer data</li>
        </ol>
      </div>
      )}

      {/* Mobile cards */}
      {isMobile && loading && <div className="flex justify-center py-10"><div className="spinner" /></div>}
      {isMobile && !loading && templates.length === 0 && (
        <div className="empty-state">
          <p style={{ color: 'var(--text-tertiary)' }}>No templates registered yet — click "Register Template" to add one.</p>
        </div>
      )}
      {isMobile && !loading && templates.length > 0 && (
        <div className="flex flex-col gap-2">
          {templates.map(t => {
            const ext = t as WhatsAppTemplate & { purpose?: string }
            const expanded = expandedCardId === t.id
            return (
              <div key={t.id} className="card" style={{ padding: 0, overflow: 'hidden', opacity: t.is_active ? 1 : 0.6 }}>
                <div
                  className="flex items-center gap-2.5 px-3 py-3 cursor-pointer"
                  onClick={() => setExpandedCardId(expanded ? null : t.id)}
                >
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-mono font-medium truncate" style={{ color: 'var(--text-primary)' }}>{t.name}</p>
                    <div className="flex items-center gap-1.5 mt-0.5">
                      <span className="badge-neutral capitalize" style={{ fontSize: 10 }}>{t.category}</span>
                      <span className={t.is_active ? 'badge-success' : 'badge-neutral'} style={{ fontSize: 10 }}>
                        {t.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </div>
                  </div>
                  <ChevronDown
                    className="h-4 w-4 shrink-0 transition-transform"
                    style={{ transform: expanded ? 'rotate(180deg)' : 'none', color: 'var(--text-tertiary)' }}
                  />
                </div>
                {expanded && (
                  <div style={{ borderTop: '1px solid var(--border-default)', padding: '10px 12px', background: 'var(--surface-subtle)' }}>
                    {ext.purpose && (
                      <div className="flex justify-between text-xs py-1">
                        <span style={{ color: 'var(--text-tertiary)' }}>Purpose</span>
                        <span style={{ color: 'var(--text-primary)', fontWeight: 500, maxWidth: '60%', textAlign: 'right' }}>{ext.purpose}</span>
                      </div>
                    )}
                    <div className="flex justify-between text-xs py-1">
                      <span style={{ color: 'var(--text-tertiary)' }}>Language</span>
                      <span style={{ color: 'var(--text-primary)', fontWeight: 500 }}>{t.language}</span>
                    </div>
                    <div className="flex gap-2 mt-3">
                      <button
                        className="flex-1 text-xs py-2 rounded-md border"
                        style={{ borderColor: 'var(--brand-primary)', color: 'var(--brand-primary)', background: 'transparent' }}
                        onClick={e => { e.stopPropagation(); setFormTarget(t) }}
                      >
                        <Pencil className="h-3.5 w-3.5 inline mr-1" /> Edit
                      </button>
                      <button
                        className="flex-1 text-xs py-2 rounded-md border"
                        style={{ borderColor: 'var(--border-default)', color: 'var(--text-secondary)', background: 'transparent' }}
                        disabled={toggling === t.id}
                        onClick={e => { e.stopPropagation(); handleToggle(t) }}
                      >
                        {t.is_active ? 'Deactivate' : 'Activate'}
                      </button>
                      {!t.is_active && (
                        <button
                          className="flex-1 text-xs py-2 rounded-md border"
                          style={{ borderColor: 'var(--danger-text)', color: 'var(--danger-text)', background: 'transparent' }}
                          disabled={deleting === t.id}
                          onClick={e => { e.stopPropagation(); handleDelete(t) }}
                        >
                          <Trash2 className="h-3.5 w-3.5 inline mr-1" /> Delete
                        </button>
                      )}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}

      {/* Table (desktop only) */}
      {!isMobile && <div className="table-card" style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
        <table className="data-table">
            <thead style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--surface-subtle)' }}>
              <tr>
                <th>Template Name</th>
                <th>Purpose</th>
                <th>Category</th>
                <th>Language</th>
                <th>Variables</th>
                <th>Status</th>
                <th style={{ whiteSpace: 'nowrap', width: 90 }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} className="py-12 text-center"><div className="spinner mx-auto" /></td></tr>
              ) : templates.length === 0 ? (
                <tr>
                  <td colSpan={7} className="py-12 text-center" style={{ color: 'var(--text-tertiary)' }}>
                    No templates registered yet — click "Register Template" to add one.
                  </td>
                </tr>
              ) : (
                templates.map(t => {
                  const ext   = t as WhatsAppTemplate & { purpose?: string }
                  const mapping = (t.variable_mapping ?? {}) as Record<string, string>
                  const vars  = Object.entries(mapping).filter(([, v]) => v).sort(([a], [b]) => Number(a) - Number(b))
                  return (
                    <tr key={t.id} style={{ opacity: t.is_active ? 1 : 0.5 }}>
                      <td>
                        <span className="font-mono text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                          {t.name}
                        </span>
                      </td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13, maxWidth: 200 }}>
                        {ext.purpose || <span style={{ color: 'var(--text-tertiary)' }}>—</span>}
                      </td>
                      <td><span className="badge-neutral capitalize">{t.category}</span></td>
                      <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                        {LANGUAGES.find(l => l.value === t.language)?.label ?? t.language}
                      </td>
                      <td style={{ maxWidth: 260 }}>
                        {vars.length === 0
                          ? <span style={{ color: 'var(--text-tertiary)', fontSize: 13 }}>None</span>
                          : (
                            <div className="flex flex-wrap gap-1" style={{ overflow: 'hidden', minWidth: 0 }}>
                              {(expandedVarsIds.has(t.id) ? vars : vars.slice(0, 2)).map(([pos, field]) => (
                                <span key={pos}
                                  className="text-xs px-1.5 py-0.5 rounded"
                                  style={{ background: 'var(--surface-subtle)', color: 'var(--text-secondary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: '100%', minWidth: 0 }}
                                  title={`{{${pos}}} → ${getCrmLabel(field)}`}
                                >
                                  <span className="font-mono" style={{ color: 'var(--brand-primary)' }}>{`{{${pos}}}`}</span>
                                  {' → '}
                                  {getCrmLabel(field)}
                                </span>
                              ))}
                              {!expandedVarsIds.has(t.id) && vars.length > 2 && (
                                <button
                                  onClick={() => toggleExpandedVars(t.id)}
                                  className="text-xs px-1.5 py-0.5 rounded cursor-pointer"
                                  style={{ background: 'var(--surface-subtle)', color: 'var(--brand-primary)', border: '1px solid var(--border-default)', whiteSpace: 'nowrap' }}
                                >
                                  +{vars.length - 2} more
                                </button>
                              )}
                              {expandedVarsIds.has(t.id) && vars.length > 2 && (
                                <button
                                  onClick={() => toggleExpandedVars(t.id)}
                                  className="text-xs px-1.5 py-0.5 rounded cursor-pointer"
                                  style={{ background: 'var(--surface-subtle)', color: 'var(--text-tertiary)', border: '1px solid var(--border-default)', whiteSpace: 'nowrap' }}
                                >
                                  Show less
                                </button>
                              )}
                            </div>
                          )
                        }
                      </td>
                      <td>
                        <span className={t.is_active ? 'badge-success' : 'badge-neutral'}>
                          {t.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap', width: 110 }}>
                        <div className="flex items-center gap-1">
                          <button className="btn-icon" title="Edit" onClick={() => setFormTarget(t)}>
                            <Pencil className="h-4 w-4" style={{ color: 'var(--brand-primary)' }} />
                          </button>
                          <button
                            className="btn-icon"
                            title={t.is_active ? 'Deactivate' : 'Activate'}
                            disabled={toggling === t.id}
                            onClick={() => handleToggle(t)}
                          >
                            {toggling === t.id
                              ? <div className="spinner" style={{ width: 16, height: 16 }} />
                              : t.is_active
                                ? <ToggleRight className="h-4 w-4" style={{ color: 'var(--success-text)' }} />
                                : <ToggleLeft className="h-4 w-4" style={{ color: 'var(--text-tertiary)' }} />
                            }
                          </button>
                          {!t.is_active && (
                            <button
                              className="btn-icon"
                              title="Delete"
                              disabled={deleting === t.id}
                              onClick={() => handleDelete(t)}
                            >
                              {deleting === t.id
                                ? <div className="spinner" style={{ width: 16, height: 16 }} />
                                : <Trash2 className="h-4 w-4" style={{ color: 'var(--danger-text)' }} />
                              }
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )
                })
              )}
            </tbody>
          </table>
      </div>}

      {formTarget !== undefined && (
        <TemplateFormModal
          initial={formTarget === 'new' ? null : formTarget}
          onClose={() => setFormTarget(undefined)}
          onSaved={handleSaved}
        />
      )}
    </div>
  )
}
